import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  /** Ícone do `lucide-react`. */
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  title: string;
  /** O que fazer a respeito. Uma tela vazia sem saída é um beco. */
  description?: ReactNode;
  /** Botão da ação principal, quando quem está vendo pode resolver. */
  action?: ReactNode;
  className?: string;
}

/**
 * Estado vazio padrão.
 *
 * Existe fora do `DataList` porque nem todo vazio é de listagem: aba sem conteúdo,
 * resultado de busca e painel sem dado usam o mesmo desenho.
 *
 * Não é a tela de erro. Vazio significa "não há nada aqui, e talvez você possa criar";
 * erro significa "não deu para saber". Tratar os dois com o mesmo componente é o que faz
 * uma falha de rede parecer uma base vazia.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      {Icon && (
        <div className="rounded-full bg-muted p-3">
          <Icon className="size-6 text-muted-foreground" aria-hidden />
        </div>
      )}

      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        {description && (
          <p className="mx-auto max-w-prose text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {action}
    </div>
  );
}
